import { inject, injectable } from 'inversify';
import { TYPES } from '@Types';
import { Result } from '@Redux/type';
import { RequestAction, RequestFailure } from '@Repository/RequestAction';
import {
  ArmyListUnit,
  UserFavoriteUnit,
  UserFavoriteUnitOption,
} from '@Models';
import { UserFavoriteUnitRepository } from '@Repository/ninthAge';
import { ICrudEntitiesAction } from '@Redux/ICrudEntitiesAction';
import userFavoriteUnitsSlice from '@Redux/reducers/ninthage/user-favorite-units.slice';
import { createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  RequestUserFavoriteUnitsSuccess,
  RequestUserFavoriteUnitSuccess,
} from '@Repository/ninthAge/user-favorite/response';
import { ArmyListUnitAction } from '@Redux/actions';
import { container } from '@Container';
import toast from 'react-hot-toast';
import armyListUnitSlice from '@Redux/reducers/builder/armyListUnit.slice';

@injectable()
export class UserFavoriteUnitsAction extends ICrudEntitiesAction<UserFavoriteUnit> {
  @inject(TYPES.Repository.UserFavorite.Unit)
  private _repository!: UserFavoriteUnitRepository;

  public delete = createAsyncThunk(
    `${this.constructor.name}/delete`,
    async ({ unitId }: { unitId: string }, thunkAPI) => {
      thunkAPI.dispatch(this.areLoading(true));

      const response = await this._repository.delete(unitId, { locale: null });

      if (response instanceof RequestFailure) {
        thunkAPI.dispatch(this.areLoading(false));
        toast.error('Impossible to delete unit from favorites');
        return;
      }
      toast.success('Favorite unit deleted');
      thunkAPI.dispatch(
        userFavoriteUnitsSlice.actions.removeUserFavoriteUnit(unitId),
      );
      thunkAPI.dispatch(this.areLoading(false));
    },
  );

  public fetchData =
    (id: number): Result<void> =>
    async (dispatch) => {
      dispatch(this.areLoading(true));

      const cachedItems = await this.getCachedItems(id);
      if (cachedItems != null) dispatch(this.cacheDataSuccess(cachedItems));

      const response = await this.getItems(id);

      if (response instanceof RequestFailure) {
        const failure = response as RequestFailure;

        dispatch(this.haveError(failure.error.message));
        dispatch(this.areLoading(false));
        return;
      }

      const success = response as RequestUserFavoriteUnitsSuccess;
      dispatch(this.fetchDataSuccess(success.payload));
      dispatch(this.areLoading(false));
    };

  public create =
    (armyListUnit: ArmyListUnit, name: string): Result<void> =>
    async (dispatch) => {
      const armyListUnitAction: ArmyListUnitAction =
        container.get<ArmyListUnitAction>(TYPES.Actions.ArmyListUnitAction);

      dispatch(armyListUnitAction.areLoading(true));

      const favoriteUnit = {
        id: '',
        name: name,
        unitId: armyListUnit.unit_id,
        unitOptions: armyListUnit.army_list_unit_unit_options.map(
          (option) =>
            ({
              id: '',
              unitOptionId: option.unit_option_id,
              quantity: option.quantity,
            }) satisfies UserFavoriteUnitOption as UserFavoriteUnitOption,
        ),
      } satisfies UserFavoriteUnit as UserFavoriteUnit;

      const response = await this._repository.create(favoriteUnit, {
        locale: null,
      });

      if (response instanceof RequestFailure) {
        dispatch(armyListUnitSlice.actions.cancel());
        dispatch(armyListUnitAction.areLoading(false));
        toast.error('Impossible to add unit to favorites');
        return;
      }

      dispatch(armyListUnitSlice.actions.cancel());
      dispatch(
        userFavoriteUnitsSlice.actions.addUserFavoriteUnit(
          (response as RequestUserFavoriteUnitSuccess).payload,
        ),
      );
      dispatch(armyListUnitAction.areLoading(false));

      toast.success('Unit added to favorites');
    };

  protected fetchDataSuccess(
    items: UserFavoriteUnit[],
  ): PayloadAction<UserFavoriteUnit[]> {
    return userFavoriteUnitsSlice.actions.setUserFavoriteUnits(items);
  }

  protected cacheDataSuccess(
    items: UserFavoriteUnit[],
  ): PayloadAction<UserFavoriteUnit[]> {
    return userFavoriteUnitsSlice.actions.setUserFavoriteUnits(items);
  }

  protected haveError(message: string): PayloadAction<string> {
    return userFavoriteUnitsSlice.actions.setError(message);
  }

  protected areLoading(bool: boolean): PayloadAction<boolean> {
    return userFavoriteUnitsSlice.actions.setLoading(bool);
  }

  protected getCachedItems(id: number): Promise<UserFavoriteUnit[] | null> {
    // TODO
    return Promise.resolve(null);
  }

  protected getItems(id: number): Promise<RequestAction> {
    return this._repository.getAll(id, { locale: null });
  }
}
